/**
 * Recorre la flota lote por lote y va acumulando.
 *
 * El servidor entrega de a pocos vehículos porque un Worker no alcanza a traer
 * treinta historiales en una sola petición. Esto es la otra mitad: pide, guarda,
 * y vuelve a pedir con el cursor hasta que ya no venga ninguno.
 *
 * Tres cosas que importan y que por eso están aquí y no repetidas en cada
 * pantalla:
 *
 *  - **Se ve avanzar.** Cada lote actualiza el progreso, así que treinta
 *    vehículos no son un minuto de pantalla congelada.
 *  - **Se puede detener.** Un recorrido largo que no se puede parar es una
 *    pantalla que hay que recargar.
 *  - **Lo que se acumula es lo que llegó.** Si un lote trae fallas, quedan a la
 *    vista; no se completa a mano ni se descarta lo demás.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api-client.ts";

export type BatchFailure = { deviceId: string; plate: string | null; reason: string };

export type BatchPage<T> = {
  items: T[];
  failures: BatchFailure[];
  nextCursor: string | null;
  total: number;
  processed: number;
};

export type BatchState<T> = {
  items: T[];
  failures: BatchFailure[];
  processed: number;
  total: number;
  running: boolean;
  /** `true` cuando se recorrió la flota entera sin interrumpir. */
  complete: boolean;
  error: string | null;
  /** Cuántos lotes se pidieron. Útil para saber si empezó siquiera. */
  batches: number;
};

const EMPTY: BatchState<never> = {
  items: [],
  failures: [],
  processed: 0,
  total: 0,
  running: false,
  complete: false,
  error: null,
  batches: 0,
};

/** Tope de lotes por recorrido. Si se alcanza, hay un cursor que no avanza. */
const MAX_BATCHES = 200;

export function useBatchWalk<T>(
  fetchPage: (input: { cursor: string | null; fromIso: string; toIso: string }) => Promise<BatchPage<T>>,
) {
  const [state, setState] = useState<BatchState<T>>(EMPTY as BatchState<T>);
  // Un token por recorrido: si se empieza otro, el anterior deja de escribir.
  const runToken = useRef(0);

  // Al desmontar, el recorrido en curso deja de escribir en un componente que
  // ya no existe.
  useEffect(() => () => void runToken.current++, []);

  const stop = useCallback(() => {
    runToken.current++;
    setState((previous) => ({ ...previous, running: false }));
  }, []);

  const reset = useCallback(() => {
    runToken.current++;
    setState(EMPTY as BatchState<T>);
  }, []);

  const start = useCallback(
    async (period: { fromIso: string; toIso: string }) => {
      const token = ++runToken.current;
      setState({ ...(EMPTY as BatchState<T>), running: true });

      let cursor: string | null = null;
      const items: T[] = [];
      const failures: BatchFailure[] = [];

      for (let batch = 0; batch < MAX_BATCHES; batch++) {
        let page: BatchPage<T>;
        try {
          page = await fetchPage({ cursor, ...period });
        } catch (error) {
          if (runToken.current !== token) return;
          setState({
            items,
            failures,
            processed: items.length + failures.length,
            total: 0,
            running: false,
            complete: false,
            error: error instanceof ApiError ? error.message : "Ocurrió un error inesperado.",
            batches: batch,
          });
          return;
        }
        // El recorrido se detuvo o se reinició mientras esta petición volvía.
        if (runToken.current !== token) return;

        items.push(...page.items);
        failures.push(...page.failures);
        cursor = page.nextCursor;

        const done = cursor === null;
        setState({
          items: [...items],
          failures: [...failures],
          processed: page.processed,
          total: page.total,
          running: !done,
          complete: done,
          error: null,
          batches: batch + 1,
        });
        if (done) return;
      }

      if (runToken.current !== token) return;
      setState((previous) => ({
        ...previous,
        running: false,
        error: "El recorrido se detuvo por seguridad: el cursor no avanzó como debía.",
      }));
    },
    [fetchPage],
  );

  return { ...state, start, stop, reset };
}

/** Adapta una respuesta del contrato al `BatchPage` que espera el recorrido. */
export function toBatchPage<T>(
  response: { failures: BatchFailure[]; nextCursor: string | null; total: number; processed: number },
  items: T[],
): BatchPage<T> {
  return {
    items,
    failures: response.failures,
    nextCursor: response.nextCursor,
    total: response.total,
    processed: response.processed,
  };
}